import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../shell/auth/auth.service';
import type {
  BindGateRequest,
  GateSessionDto,
  VendorGateDutyContextDto,
} from '../../sdk/one-facility/model/gateSessionDto';
import { GateSessionControllerService } from '../../sdk/one-facility/api/gateSessionController.service';

interface OneApiEnvelope<T> {
  data?: T;
  message?: string;
}

const GATE_BIND_STORAGE_KEY = 'one-axis-services-gate-bind';

@Injectable({ providedIn: 'root' })
export class ServicesGateDutyService {
  private readonly auth = inject(AuthService);
  private readonly gateApi = inject(GateSessionControllerService);

  async resolveProjectId(): Promise<number | null> {
    const user = this.auth.getStoredUser();
    const scopeProject = user?.userScope?.projectIds?.[0];
    if (scopeProject != null && scopeProject > 0) {
      return scopeProject;
    }
    return null;
  }

  resolveOrganizationId(): number | null {
    const user = this.auth.getStoredUser();
    const orgId = user?.organizationId != null ? Number(user.organizationId) : NaN;
    return Number.isFinite(orgId) && orgId > 0 ? orgId : null;
  }

  async getGateDutyContext(): Promise<VendorGateDutyContextDto> {
    const orgId = this.resolveOrganizationId();
    const projectId = await this.resolveProjectId();
    if (orgId == null || projectId == null) {
      return { gateDutyRequired: false };
    }
    const res = await firstValueFrom(
      this.gateApi.getGateDutyContext(orgId, projectId),
    );
    return unwrapEnvelope<VendorGateDutyContextDto>(res) ?? { gateDutyRequired: false };
  }

  async bindGate(bindQrToken: string): Promise<GateSessionDto> {
    const orgId = this.resolveOrganizationId();
    const projectId = await this.resolveProjectId();
    if (orgId == null || projectId == null) {
      throw new Error('Project context is required to bind a gate');
    }
    const body: BindGateRequest = { projectId, bindQrToken: bindQrToken.trim() };
    const res = await firstValueFrom(this.gateApi.bindGate(orgId, body));
    const session = unwrapEnvelope<GateSessionDto>(res);
    if (!session?.accessPointId) {
      throw new Error('Gate bind failed');
    }
    this.persistGateBind(session);
    return session;
  }

  readPersistedGateBind(): GateSessionDto | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    try {
      const raw = localStorage.getItem(GATE_BIND_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as GateSessionDto) : null;
    } catch {
      return null;
    }
  }

  clearPersistedGateBind(): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(GATE_BIND_STORAGE_KEY);
    }
  }

  private persistGateBind(session: GateSessionDto): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(GATE_BIND_STORAGE_KEY, JSON.stringify(session));
  }
}

function unwrapEnvelope<T>(res: unknown): T | null {
  if (res == null || typeof res !== 'object') {
    return null;
  }
  const data = (res as OneApiEnvelope<T>).data;
  return data ?? null;
}
